import { arrayMove } from '@dnd-kit/sortable';

/**
 * Helpers d'arbre de workflow — fonctions pures, immuables.
 *
 * Un arbre = tableau ordonné de nœuds. Un nœud est :
 *   action   : { id, type:'action', title, description, automatable, responsible,
 *                estimated_minutes, link:{kind,value,label}|null, completion_condition }
 *   décision : { id, type:'decision', question, answers:[ { id, label, color, steps:[…] } ] }
 *
 * L'éditeur détient l'arbre `steps` complet ; chaque modification produit un NOUVEL
 * arbre (jamais de mutation en place) via ces helpers, repérés par id de nœud.
 */

const PALETTE = ['#16a34a', '#dc2626', '#d97706', '#2563eb', '#7c3aed', '#0891b2', '#db2777', '#65a30d'];

export function uid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Repli (navigateurs très anciens) — suffisant pour des ids de nœuds.
  return 'id-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function newActionNode() {
  return {
    id: uid(),
    type: 'action',
    title: '',
    description: '',
    automatable: false,
    responsible: '',
    estimated_minutes: null,
    link: null,
    completion_condition: '',
  };
}

export function newAnswer(index = 0) {
  return { id: uid(), label: '', color: PALETTE[index % PALETTE.length], steps: [] };
}

export function newDecisionNode() {
  return {
    id: uid(),
    type: 'decision',
    question: '',
    answers: [newAnswer(0), newAnswer(1)], // deux réponses par défaut (ex. Oui / Non)
  };
}

/** Applique récursivement `fn(nodes)` à chaque tableau de frères (racine + chaque branche). */
function mapBranches(nodes, fn) {
  const next = fn(nodes);
  return next.map(node => {
    if (node && node.type === 'decision' && Array.isArray(node.answers)) {
      return {
        ...node,
        answers: node.answers.map(ans => ({ ...ans, steps: mapBranches(ans.steps || [], fn) })),
      };
    }
    return node;
  });
}

/** Met à jour le nœud d'id `id` en lui appliquant `patch` (objet ou fonction node→node). */
export function updateNodeById(nodes, id, patch) {
  return mapBranches(nodes, list =>
    list.map(node => {
      if (node.id !== id) return node;
      const p = typeof patch === 'function' ? patch(node) : patch;
      return { ...node, ...p };
    })
  );
}

/** Supprime le nœud d'id `id` où qu'il soit dans l'arbre. */
export function removeNodeById(nodes, id) {
  return mapBranches(nodes, list => list.filter(node => node.id !== id));
}

/**
 * Insère `node` dans un tableau de frères.
 * @param parentId id du nœud décision parent, ou null pour la racine
 * @param answerId id de la réponse (branche) si parentId est une décision
 * @param index position d'insertion (par défaut : fin)
 */
export function insertNode(nodes, node, parentId, answerId, index) {
  if (parentId == null) {
    const copy = [...nodes];
    copy.splice(index == null ? copy.length : index, 0, node);
    return copy;
  }
  return updateNodeById(nodes, parentId, parent => {
    if (parent.type !== 'decision') return parent;
    return {
      ...parent,
      answers: parent.answers.map(ans => {
        if (ans.id !== answerId) return ans;
        const steps = [...(ans.steps || [])];
        steps.splice(index == null ? steps.length : index, 0, node);
        return { ...ans, steps };
      }),
    };
  });
}

/** Réordonne les frères d'une branche (racine si parentId/answerId nuls). */
export function reorderSiblings(nodes, parentId, answerId, from, to) {
  if (parentId == null) return arrayMove(nodes, from, to);
  return updateNodeById(nodes, parentId, parent => {
    if (parent.type !== 'decision') return parent;
    return {
      ...parent,
      answers: parent.answers.map(ans =>
        ans.id === answerId ? { ...ans, steps: arrayMove(ans.steps || [], from, to) } : ans
      ),
    };
  });
}

/** Gestion des réponses d'une décision. */
export function addAnswer(nodes, decisionId) {
  return updateNodeById(nodes, decisionId, d => ({
    answers: [...(d.answers || []), newAnswer((d.answers || []).length)],
  }));
}
export function updateAnswer(nodes, decisionId, answerId, patch) {
  return updateNodeById(nodes, decisionId, d => ({
    answers: (d.answers || []).map(a => (a.id === answerId ? { ...a, ...patch } : a)),
  }));
}
export function removeAnswer(nodes, decisionId, answerId) {
  return updateNodeById(nodes, decisionId, d => ({
    answers: (d.answers || []).filter(a => a.id !== answerId),
  }));
}

/** Compteurs : nb total d'actions (toutes branches) + somme des durées. */
export function computeCounts(nodes) {
  let stepCount = 0;
  let totalMinutes = 0;
  const walk = (list) => {
    (list || []).forEach(node => {
      if (!node) return;
      if (node.type === 'action') {
        stepCount += 1;
        totalMinutes += Number(node.estimated_minutes) || 0;
      } else if (node.type === 'decision') {
        (node.answers || []).forEach(a => walk(a.steps));
      }
    });
  };
  walk(nodes);
  return { step_count: stepCount, total_estimated_minutes: totalMinutes };
}

/**
 * Miroir frontend de l'engine backend : liste plate ordonnée des étapes visibles.
 * Une décision est toujours visible ; ses enfants seulement pour la réponse choisie.
 * Chaque item est annoté (purement visuel) avec sa branche parente pour regrouper en
 * lanes : `branch = { answerLabel, answerColor }` (null à la racine) et `branchStart`
 * = true pour le premier item d'une branche.
 * @returns {Array<{id, type, node, depth, branch, branchStart}>}
 */
export function computeVisibleSteps(nodes, answers) {
  const out = [];
  const visited = new Set();
  answers = answers || {};
  // branch = { answerLabel, answerColor } de la réponse parente, ou null à la racine
  const walk = (list, depth, branch) => {
    (list || []).forEach((node, idx) => {
      if (!node || !node.id || visited.has(node.id)) return;
      visited.add(node.id);
      const base = { id: node.id, node, depth, branch: branch || null, branchStart: !!branch && idx === 0 };
      if (node.type === 'action') {
        out.push({ ...base, type: 'action' });
      } else if (node.type === 'decision') {
        out.push({ ...base, type: 'decision' });
        const chosen = answers[node.id];
        if (chosen != null) {
          const ans = (node.answers || []).find(a => a && a.id === chosen);
          if (ans) walk(ans.steps, depth + 1, { answerLabel: ans.label || '—', answerColor: ans.color });
        }
      }
    });
  };
  walk(nodes, 0, null);
  return out;
}

export const ANSWER_PALETTE = PALETTE;
